import {
  BuildContext,
  ExportOptions,
  ExportPayloadChildSection,
  ExportPayloadSection,
} from '../../types';
import buildXmlElement from './buildXmlElement';
import escapeXml from './escapeXml';
import reportProgressAndMaybeYield from '../reportProgressAndMaybeYield';
import resolveTextCellValue from '../resolveTextCellValue';
import resolveXmlElementNames from './resolveXmlElementNames';
import stripControlChars from '../stripControlChars';

const appendXmlRows = async (
  targetLines: string[],
  rows: Record<string, unknown>[],
  columns: ExportPayloadSection['columns'],
  options: ExportOptions,
  indent: string,
  context: BuildContext,
  childSections: ExportPayloadChildSection[] = [],
  masterIdItem?: string
): Promise<void> => {
  // Resolved once so the <names> legend and every row share the same disambiguated elements.
  const elementNames = resolveXmlElementNames(columns);

  if (options.includeColumnNames && columns.length > 0) {
    targetLines.push(`${indent}<names>`);
    columns.forEach((column, index) => {
      targetLines.push(
        buildXmlElement(elementNames[index], column.label, `${indent}  `)
      );
    });
    targetLines.push(`${indent}</names>`);
  }

  // Append directly so large nested XML exports do not allocate intermediate row arrays.
  for (const row of rows) {
    const masterId = masterIdItem ? String(row[masterIdItem] ?? '') : '';

    targetLines.push(`${indent}<row>`);

    columns.forEach((column, index) => {
      // XML exports use dataset item names as element names to match legacy samples.
      targetLines.push(
        buildXmlElement(
          elementNames[index],
          stripControlChars(resolveTextCellValue(row, column, options)),
          `${indent}  `
        )
      );
    });

    for (const childSection of childSections) {
      const childRows = childSection.rowsByMasterId.get(masterId) ?? [];

      // An empty subset still carries its <names> legend, so only self-close when nothing can go inside.
      if (childRows.length === 0 && !options.includeColumnNames) {
        targetLines.push(
          `${indent}  <slave name="${escapeXml(childSection.id)}"/>`
        );
        continue;
      }

      targetLines.push(
        `${indent}  <slave name="${escapeXml(childSection.id)}">`
      );
      await appendXmlRows(
        targetLines,
        childRows,
        childSection.columns,
        options,
        `${indent}    `,
        context
      );
      targetLines.push(`${indent}  </slave>`);
    }

    targetLines.push(`${indent}</row>`);
    await reportProgressAndMaybeYield(context);
  }
};

export default appendXmlRows;
